import { Router } from '@angular/router';

import { Config } from './../../config/config';
import { isCompanyProtectedRoute } from './route.utils';

export const OIDC_STORAGE_KEY = 'mw-redirect';
const AUTH_LAST_ACTIVE_COMPANY_ID = 'mw-auth-last-active-company-id';
const AUTH_CURRENT_OIDC_CONFIG_ID = 'mw-auth-current-oidc-config-id';
const AUTH_USE_LOCAL_COMPANY_SELECTOR = 'mw-auth-use-local-company-selector';

export class LocalStorageService {
  static getStoredRedirectRoute(): string {
    return localStorage.getItem(OIDC_STORAGE_KEY) || '';
  }
  static saveStoredRedirectRoute(url: string, force: boolean = false): void {
    const existingUrl = localStorage.getItem(OIDC_STORAGE_KEY);

    if (!existingUrl || force) {
      localStorage.setItem(OIDC_STORAGE_KEY, url);
    }
  }
  static deleteStoredRedirectRoute(): void {
    localStorage.removeItem(OIDC_STORAGE_KEY);
  }
  static tryToRestoreRoute(
    config: Config | null,
    router: Router,
    hasAccessToModule: boolean
  ): void {
    if (config === null) return;

    const protectedRoute = isCompanyProtectedRoute(
      config.environment.skipCompanyProtectionRoutes || []
    );
    const hasAccess = !protectedRoute || hasAccessToModule === true;
    const storedRoute = LocalStorageService.getStoredRedirectRoute();

    if (storedRoute && document.location.href.endsWith(storedRoute)) {
      return;
    }

    if (storedRoute && hasAccess) {
      router
        .navigate([storedRoute])
        .then(() => this.deleteStoredRedirectRoute());
    }
  }
  static deleteLastActiveCompanyId(): void {
    localStorage.removeItem(AUTH_LAST_ACTIVE_COMPANY_ID);
  }
  static setLastActiveCompanyId(companyId: number): void {
    localStorage.setItem(AUTH_LAST_ACTIVE_COMPANY_ID, String(companyId));
  }
  static getLastActiveCompanyId(): number {
    return Number(localStorage.getItem(AUTH_LAST_ACTIVE_COMPANY_ID));
  }
  static getCurrentOIDCConfigurationId(): string | null {
    return localStorage.getItem(AUTH_CURRENT_OIDC_CONFIG_ID);
  }
  static setCurrentOIDCConfigurationId(configId: string): void {
    return localStorage.setItem(AUTH_CURRENT_OIDC_CONFIG_ID, configId);
  }
  static getUseLocalCompanySelector(): boolean {
    const value = localStorage.getItem(AUTH_USE_LOCAL_COMPANY_SELECTOR);
    return (value && Boolean(value)) || false;
  }
  static setUseLocalCompanySelector(useLocal: boolean): void {
    return localStorage.setItem(
      AUTH_USE_LOCAL_COMPANY_SELECTOR,
      String(useLocal)
    );
  }

  static setItemWithExpiry(
    key: string,
    value: string,
    timeoutInSeconds: number
  ): void {
    const now = new Date();
    const item = {
      value: value,
      expiry: now.getTime() + timeoutInSeconds * 1000,
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  static getItemWithExpiry(key: string): string | null {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) {
      return null;
    }
    try {
      const item = JSON.parse(itemStr);
      const now = new Date();
      if (now.getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    } catch (_) {
      // just in case of invalid JSON
      localStorage.removeItem(key);
      return null;
    }
  }

  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}
